import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Heart, Send, Loader2, VenetianMask } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";

const PLATFORMS = ["Instagram", "TikTok", "Facebook", "X (Twitter)", "Snapchat", "Telegram", "WhatsApp", "Other"];
const COST = 50;

export default function SocialInviteDialog({ companion }) {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [inviteType, setInviteType] = useState("secret_admirer");
  const [platform, setPlatform] = useState("Instagram");
  const [handle, setHandle] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!handle.trim()) {
      toast.error(t("si.errHandle"));
      return;
    }
    setSending(true);
    try {
      await api.post("/social-invites", {
        companion_id: companion.user_id,
        invite_type: inviteType,
        platform,
        handle: handle.trim(),
        note,
      });
      toast.success(t("si.success", { name: companion.name }));
      window.dispatchEvent(new Event("si-updated"));
      setOpen(false);
      setHandle("");
      setNote("");
      checkAuth();
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error(t("prof.tNoCredits"));
        navigate("/credits");
      } else {
        toast.error(error.response?.data?.detail || t("prof.tRequestFail"));
      }
    } finally {
      setSending(false);
    }
  };

  const typeBtn = (value, label) => (
    <button
      type="button"
      onClick={() => setInviteType(value)}
      className={`flex-1 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
        inviteType === value
          ? "border-primary bg-primary/15 text-primary"
          : "border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10"
      }`}
      data-testid={`si-type-${value}`}
    >
      {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="border-violet-500/40 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 hover:text-violet-200"
          data-testid="social-invite-btn"
        >
          <VenetianMask className="w-4 h-4 mr-2" />
          {t("si.button")}
          <span className="ml-2 text-xs opacity-80">🪙 {COST}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md bg-card border-white/10" data-testid="social-invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary fill-primary/30" />
            {t("si.title", { name: companion.name })}
          </DialogTitle>
          <DialogDescription>{t("si.desc", { name: companion.name })}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm mb-2 block">{t("si.typeLabel")}</Label>
            <div className="flex gap-2">
              {typeBtn("secret_admirer", t("si.typeAdmirer"))}
              {typeBtn("date_request", t("si.typeDate"))}
            </div>
          </div>
          <div>
            <Label className="text-sm mb-2 block">{t("si.platform")}</Label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger data-testid="si-platform-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLATFORMS.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm mb-2 block">{t("si.handle")}</Label>
            <Input
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="@username"
              data-testid="si-handle-input"
            />
          </div>
          <div>
            <Label className="text-sm mb-2 block">{t("si.note")}</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 300))}
              placeholder={t("si.notePh", { name: companion.name })}
              rows={2}
              data-testid="si-note-input"
            />
          </div>
          <Button onClick={submit} disabled={sending} className="w-full" data-testid="si-submit-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {t("si.submit", { n: COST })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
